import { Fragment, useEffect, useRef, useState } from "react";
import { useIntegrateModal } from "../../context/IntegrateModalContext";
import { submitIntegrateForm, HONEYPOT_FIELD } from "../../utils/submitIntegrateForm";
import { getTurnstileSiteKey } from "../../config/turnstile";
import TurnstileWidget from "./TurnstileWidget";
import "./IntegrateModal.css";

/* ───────── Form schema per submission type ─────────────── */

const TOKEN_LISTING_FEE = {
  amount: "0.1 ETH",
  wallet: "0xfa7ffdf2fdec8cc2edb56c42795419e509e80b6d",
};

const NEW_CHAIN_REQUEST = "__new_chain_request__";

const TOKEN_CHAIN_OPTIONS = [
  { value: NEW_CHAIN_REQUEST, label: "New Blockchain Integration Request" },
  { value: "Ethereum",     label: "Ethereum" },
  { value: "BNB Chain",    label: "BNB Chain" },
  { value: "Solana",       label: "Solana" },
  { value: "Base",         label: "Base" },
  { value: "Arbitrum",     label: "Arbitrum" },
  { value: "Polygon",      label: "Polygon" },
  { value: "Avalanche",    label: "Avalanche" },
  { value: "Optimism",     label: "Optimism" },
  { value: "Cronos",       label: "Cronos" },
  { value: "Cosmos",       label: "Cosmos" },
  { value: "Osmosis",      label: "Osmosis" },
  { value: "Injective",    label: "Injective" },
  { value: "THORChain",    label: "THORChain" },
  { value: "Maya Chain",   label: "Maya Chain" },
  { value: "Bitcoin",      label: "Bitcoin" },
];

const FIELD_COMMON = [
  { name: "contactName",     label: "Your name",                 type: "text",  required: true, placeholder: "Satoshi N." },
  { name: "contactEmail",    label: "Email",                     type: "email", required: true, placeholder: "you@example.com" },
  { name: "telegramHandle",  label: "Point of Contact (Telegram)", type: "text",  required: true, placeholder: "@handle" },
];

const FORM_SCHEMAS = {
  token: {
    title: "List a token on BiorVault",
    subtitle:
      "Tell us about your project. We review every submission and will reach out by email.",
    accent: "token",
    fields: [
      { name: "projectName",    label: "Project name",     type: "text",     required: true,  placeholder: "Acme Protocol" },
      { name: "tokenSymbol",    label: "Token symbol",     type: "text",     required: true,  placeholder: "ACME" },
      { name: "contractAddress",label: "Contract address", type: "text",     required: true,  placeholder: "0x…" },
      {
        name: "chain",
        label: "Primary chain",
        type: "select",
        required: true,
        options: TOKEN_CHAIN_OPTIONS,
      },
      { name: "website",        label: "Website",          type: "url",      required: false, placeholder: "https://" },
      { name: "socials",        label: "Social links",     type: "text",     required: false, placeholder: "Twitter / Telegram / Discord" },
      ...FIELD_COMMON,
      { name: "message",        label: "Anything else we should know?", type: "textarea", required: false, rows: 4, placeholder: "Audit reports, liquidity, launch timeline…" },
      {
        name: "paymentTxUrl",
        label: "Etherscan transaction link",
        type: "url",
        required: true,
        placeholder: "https://etherscan.io/tx/0x…",
        help: "Paste the Etherscan link for your 0.1 ETH listing-fee transaction so our team can verify it.",
        showWhen: (v) => Boolean(v.chain) && v.chain !== NEW_CHAIN_REQUEST,
      },
      {
        name: "budgetRange",
        label: "Budget range",
        type: "text",
        required: true,
        placeholder: "e.g. 10k to 25k USD, flexible",
        showWhen: (v) => v.chain === NEW_CHAIN_REQUEST,
      },
    ],
  },
  dapp: {
    title: "Submit your dApp",
    subtitle:
      "Share your dApp so our users can discover it from inside BiorVault.",
    accent: "dapp",
    fields: [
      { name: "dappName",   label: "dApp name",        type: "text",     required: true,  placeholder: "My dApp" },
      { name: "dappUrl",    label: "dApp URL",         type: "url",      required: true,  placeholder: "https://app.example.com" },
      { name: "chains",     label: "Supported chains", type: "text",     required: true,  placeholder: "Ethereum, Base, Solana…" },
      { name: "category",   label: "Category",         type: "select",   required: true, options: ["DeFi", "NFT", "GameFi", "Social", "Infra", "Other"] },
      { name: "description",label: "Short description",type: "textarea", required: true,  rows: 4, placeholder: "What does it do and who is it for?" },
      ...FIELD_COMMON,
      {
        name: "role",
        label: "What's your role?",
        type: "text",
        required: true,
        placeholder: "Founder, Developer, Community Lead, Member…",
        help: "If you are a BiorLabs community member without a custom title, write \"Member\".",
      },
    ],
  },
  integration: {
    title: "Direct integration request",
    subtitle:
      "You're building on BiorVault / BiorSwap APIs? Tell us the scope and we'll route you to the right engineer.",
    accent: "integration",
    fields: [
      { name: "orgName",     label: "Company / team",      type: "text",    required: true,  placeholder: "Acme Labs" },
      { name: "product",     label: "Your product",        type: "text",    required: true,  placeholder: "Wallet, DEX, aggregator, CEX…" },
      { name: "intent",      label: "What are you integrating?", type: "select", required: true, options: ["BiorSwap API / SDK", "BiorVault widget", "Wallet connector", "Listing / liquidity", "Other"] },
      { name: "timeline",    label: "Target timeline",     type: "text",    required: false, placeholder: "Q1, Q2, ASAP…" },
      ...FIELD_COMMON,
      { name: "message",     label: "Technical scope",     type: "textarea",required: true,  rows: 4, placeholder: "Chains, pairs, volume, deadlines…" },
    ],
  },
  collab: {
    title: "Partnership & collab",
    subtitle:
      "Marketing, co-campaigns, incubations, media. Tell us what you have in mind.",
    accent: "collab",
    fields: [
      { name: "orgName",    label: "Organization",          type: "text",    required: true,  placeholder: "Your org / collective" },
      { name: "collabType", label: "Type of collab",        type: "select",  required: true, options: ["Co-marketing", "Media / content", "Event", "Token exchange", "Incubation", "Other"] },
      { name: "audience",   label: "Audience size",         type: "text",    required: false, placeholder: "Followers, members, reach…" },
      ...FIELD_COMMON,
      {
        name: "role",
        label: "What's your role?",
        type: "text",
        required: true,
        placeholder: "Founder, Community Lead, Member…",
        help: "If you are a BiorLabs community member without a custom title, write \"Member\".",
      },
      {
        name: "identityContext",
        label: "How can we verify it's you?",
        type: "textarea",
        required: true,
        rows: 4,
        placeholder: "Share links or proofs that tie this submission to you (e.g. X / Telegram profile, verified email on the org's site, a signed message, etc.)",
      },
      { name: "message",    label: "The pitch",             type: "textarea",required: true,  rows: 4, placeholder: "What are we building together?" },
      {
        name: "pitchDocs",
        label: "Pitch materials (optional)",
        type: "files",
        required: false,
        maxFiles: 3,
        maxBytes: 8 * 1024 * 1024,
        accept: ".pdf,.png,.jpg,.jpeg,.webp,.heic",
        help: "Attach a deck, one-pager, mockups, or anything that supports your pitch. Up to 3 files, 8 MB each. PDF, PNG, JPG, WEBP, or HEIC.",
      },
    ],
  },
  general: {
    title: "Get in touch with BiorLabs",
    subtitle: "Tell us a bit about what you need and we'll get back to you.",
    accent: "integration",
    fields: [
      ...FIELD_COMMON,
      { name: "topic",   label: "Topic",   type: "text",     required: true, placeholder: "Listing, integration, partnership…" },
      { name: "message", label: "Message", type: "textarea", required: true, rows: 5, placeholder: "How can we help?" },
    ],
  },
};

/* ───────── Custom dropdown ────────────────────────────── */

function CustomSelect({ id, value, options, onChange, placeholder, testId, refProp, required }) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const normalized = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o,
  );
  const selected = normalized.find((o) => o.value === value) || null;

  useEffect(() => {
    if (!open) {
      setActiveIdx(-1);
      return;
    }
    const selIdx = normalized.findIndex((o) => o.value === value);
    setActiveIdx(selIdx >= 0 ? selIdx : 0);
  }, [open, value, normalized]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const commit = (optValue) => {
    onChange({ target: { value: optValue } });
    setOpen(false);
    window.setTimeout(() => buttonRef.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
      return;
    }
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (open && e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(normalized.length - 1, i + 1));
    } else if (open && e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (open && e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0) commit(normalized[activeIdx].value);
    }
  };

  const setButtonRef = (el) => {
    buttonRef.current = el;
    if (refProp) {
      if (typeof refProp === "function") refProp(el);
      else refProp.current = el;
    }
  };

  return (
    <div
      ref={wrapRef}
      className={`biors-select ${open ? "biors-select--open" : ""}`}
    >
      <button
        id={id}
        type="button"
        ref={setButtonRef}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className="biors-select__button font-dm"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-required={required || undefined}
        data-testid={testId}
      >
        <span
          className={`biors-select__value ${selected ? "" : "biors-select__value--placeholder"}`}
        >
          {selected ? selected.label : placeholder || "Select…"}
        </span>
        <span className="biors-select__chevron" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {open ? (
        <div
          ref={panelRef}
          id={`${id}-listbox`}
          className="biors-select__panel"
          role="listbox"
          onKeyDown={onKeyDown}
        >
          <div className="biors-select__list">
            {normalized.map((o, i) => {
              const isSelected = value === o.value;
              const isActive = activeIdx === i;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => commit(o.value)}
                  className={`biors-select__option ${
                    isSelected ? "biors-select__option--selected" : ""
                  } ${isActive ? "biors-select__option--active" : ""}`}
                  data-testid={`${testId}-option-${o.value}`}
                >
                  {isSelected ? (
                    <span className="biors-select__check" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="5 12.5 10 17.5 19 7.5" />
                      </svg>
                    </span>
                  ) : (
                    <span className="biors-select__check-spacer" aria-hidden="true" />
                  )}
                  <span className="biors-select__option-label">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ───────── File dropzone ──────────────────────────────── */

const FILE_TYPE_ICON = {
  pdf: "PDF", png: "PNG", jpg: "JPG", jpeg: "JPG", webp: "WEBP", heic: "HEIC",
};

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileDropzone({
  id,
  value,
  onChange,
  maxFiles = 3,
  maxBytes = 8 * 1024 * 1024,
  accept = ".pdf,.png,.jpg,.jpeg,.webp,.heic",
  testId,
}) {
  const files = Array.isArray(value) ? value : [];
  const [localError, setLocalError] = useState("");
  const inputRef = useRef(null);

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []);
    if (!list.length) return;
    const next = [...files];
    const problems = [];
    for (const f of list) {
      if (next.length >= maxFiles) {
        problems.push(`You can attach at most ${maxFiles} files.`);
        break;
      }
      if (f.size > maxBytes) {
        problems.push(`${f.name} is larger than ${Math.round(maxBytes / (1024 * 1024))} MB.`);
        continue;
      }
      if (next.some((e) => e.name === f.name && e.size === f.size)) {
        continue;
      }
      next.push(f);
    }
    setLocalError(problems.length ? problems.join(" ") : "");
    onChange(next);
  };

  const removeAt = (idx) => {
    const next = files.filter((_, i) => i !== idx);
    onChange(next);
    setLocalError("");
  };

  const isFull = files.length >= maxFiles;

  return (
    <div className="integrate-modal__files">
      <input
        ref={inputRef}
        id={id}
        type="file"
        multiple
        accept={accept}
        className="integrate-modal__files-input"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => !isFull && inputRef.current?.click()}
        className="integrate-modal__files-button font-dm"
        disabled={isFull}
      >
        <span className="integrate-modal__files-button-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 16V4" />
            <path d="M6 10l6-6 6 6" />
            <path d="M4 20h16" />
          </svg>
        </span>
        <span className="integrate-modal__files-button-text">
          {isFull ? `${maxFiles} / ${maxFiles} attached` : files.length ? "Add another file" : "Choose files"}
        </span>
      </button>
      {files.length > 0 ? (
        <ul className="integrate-modal__files-list">
          {files.map((f, i) => {
            const ext = (f.name.split(".").pop() || "").toLowerCase();
            const badge = FILE_TYPE_ICON[ext] || "FILE";
            return (
              <li key={`${f.name}-${f.size}-${i}`} className="integrate-modal__files-item font-dm">
                <span className="integrate-modal__files-badge" aria-hidden="true">{badge}</span>
                <span className="integrate-modal__files-meta">
                  <span className="integrate-modal__files-name" title={f.name}>{f.name}</span>
                  <span className="integrate-modal__files-size">{formatBytes(f.size)}</span>
                </span>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="integrate-modal__files-remove"
                  aria-label={`Remove ${f.name}`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {localError ? (
        <span className="integrate-modal__err font-dm">{localError}</span>
      ) : null}
    </div>
  );
}

/* ───────── Modal component ────────────────────────────── */

// After a submit the button stays disabled this long, so a double click, a
// held Enter key or a script cannot fire the form again straight away.
export const SUBMIT_COOLDOWN_MS = 5000;

export function IntegrateModal() {
  const { isOpen, type, closeForm } = useIntegrateModal();
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [walletCopied, setWalletCopied] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);
  const abortRef = useRef(null);
  // Abuse protection: hidden trap field, throttle, optional Turnstile token.
  const [honeypot, setHoneypot] = useState("");
  const [cooling, setCooling] = useState(false);
  const coolingRef = useRef(false);
  const coolTimerRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [verifyError, setVerifyError] = useState(false);
  const turnstileOn = Boolean(getTurnstileSiteKey());

  useEffect(() => () => window.clearTimeout(coolTimerRef.current), []);

  const schema = FORM_SCHEMAS[type] || FORM_SCHEMAS.general;

  const copyWallet = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(TOKEN_LISTING_FEE.wallet);
      } else {
        const ta = document.createElement("textarea");
        ta.value = TOKEN_LISTING_FEE.wallet;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setWalletCopied(true);
      window.setTimeout(() => setWalletCopied(false), 1800);
    } catch (_) {
      /* noop */
    }
  };

  // Reset form whenever the modal opens/closes or the type changes
  useEffect(() => {
    if (!isOpen) {
      // Cancel any in-flight submit when the modal closes.
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      return undefined;
    }
    setStatus("idle");
    setValues({});
    setErrors({});
    setWalletCopied(false);
    setSubmitError(null);
    setHoneypot("");
    setTurnstileToken("");
    setVerifyError(false);
    const t = window.setTimeout(() => firstFieldRef.current?.focus(), 120);
    return () => window.clearTimeout(t);
  }, [isOpen, type]);

  // ESC closes the modal; lock scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeForm();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closeForm]);

  if (!isOpen) return null;

  const effectiveFields = schema.fields.filter(
    (f) => (typeof f.showWhen === "function" ? f.showWhen(values) : true),
  );
  const isTokenForm = type === "token";
  const selectedChain = values.chain || "";
  const showPaymentNotice = isTokenForm && selectedChain && selectedChain !== NEW_CHAIN_REQUEST;
  const showNewChainNotice = isTokenForm && selectedChain === NEW_CHAIN_REQUEST;

  const onChange = (name) => (e) => {
    setValues((prev) => ({ ...prev, [name]: e.target.value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const onFilesChange = (name) => (fileList) => {
    setValues((prev) => ({ ...prev, [name]: fileList }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validate = () => {
    const next = {};
    for (const f of effectiveFields) {
      if (f.required && !String(values[f.name] || "").trim()) {
        next[f.name] = "Required";
      }
      if (f.type === "email" && values[f.name]) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values[f.name])) {
          next[f.name] = "Enter a valid email";
        }
      }
      if (f.type === "url" && values[f.name]) {
        try {
          // eslint-disable-next-line no-new
          new URL(values[f.name]);
        } catch (_) {
          next[f.name] = "Enter a valid URL";
        }
      }
    }
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (coolingRef.current) return;
    const v = validate();
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }

    if (turnstileOn && !turnstileToken) {
      setVerifyError(true);
      return;
    }
    setVerifyError(false);

    // Split File arrays (from type="files" fields) out of the JSON payload.
    const scalarData = {};
    const fileBag = [];
    for (const field of effectiveFields) {
      const raw = values[field.name];
      if (raw === undefined || raw === null) continue;
      if (field.type === "files") {
        if (Array.isArray(raw)) {
          for (const f of raw) if (f) fileBag.push(f);
        }
        continue;
      }
      if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (trimmed.length > 0) scalarData[field.name] = trimmed;
      } else if (typeof raw === "number" || typeof raw === "boolean") {
        scalarData[field.name] = raw;
      }
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSubmitError(null);
    setStatus("submitting");
    coolingRef.current = true;
    setCooling(true);
    window.clearTimeout(coolTimerRef.current);
    coolTimerRef.current = window.setTimeout(() => {
      coolingRef.current = false;
      setCooling(false);
    }, SUBMIT_COOLDOWN_MS);

    const result = await submitIntegrateForm({
      type,
      data: scalarData,
      files: fileBag,
      signal: controller.signal,
      honeypot,
      turnstileToken,
    });
    // A Turnstile token works once; ask for a fresh one whatever happened.
    setTurnstileToken("");
    setTurnstileReset((n) => n + 1);

    if (controller.signal.aborted) return;
    abortRef.current = null;

    if (result.ok) {
      setStatus("success");
    } else {
      setSubmitError(result.error || "server");
      setStatus("error");
    }
  };

  const tryAgain = () => {
    setSubmitError(null);
    setTurnstileToken("");
    setStatus("idle");
  };

  return (
    <div
      className="integrate-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="integrate-modal-title"
      data-testid="integrate-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeForm();
      }}
    >
      <div
        className={`integrate-modal__panel integrate-modal__panel--${schema.accent}`}
        ref={dialogRef}
      >
        <button
          type="button"
          className="integrate-modal__close"
          aria-label="Close"
          data-testid="integrate-modal-close"
          onClick={closeForm}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <header className="integrate-modal__head">
          <span className={`integrate-modal__eyebrow integrate-modal__eyebrow--${schema.accent} font-dm`}>
            {schema.accent === "token"       && "Token Listing"}
            {schema.accent === "dapp"        && "Submit dApp"}
            {schema.accent === "integration" && "Direct Integration"}
            {schema.accent === "collab"      && "Partnership"}
          </span>
          <h2 id="integrate-modal-title" className="integrate-modal__title font-syne">
            {schema.title}
          </h2>
          <p className="integrate-modal__sub font-dm">{schema.subtitle}</p>
        </header>

        <div className="integrate-modal__body">
          {status === "success" ? (
            <div className="integrate-modal__state" data-testid="integrate-modal-success">
              <div className="integrate-modal__state-icon integrate-modal__state-icon--success" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12.5l4.5 4.5L19 7.5" />
                </svg>
              </div>
              <h3 className="integrate-modal__state-title font-syne">Message received</h3>
              <p className="integrate-modal__state-sub font-dm">
                Thanks for reaching out. A member of the BiorLabs team will review your submission
                and get back to you by email within 3 business days.
              </p>
              <div className="integrate-modal__state-actions">
                <button type="button" className="integrate-modal__btn integrate-modal__btn--ghost" onClick={closeForm}>
                  Close
                </button>
              </div>
            </div>
          ) : status === "error" ? (
            <div className="integrate-modal__state" data-testid="integrate-modal-error">
              <div className="integrate-modal__state-icon integrate-modal__state-icon--error" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v5M12 16.5v.01M4.5 19h15a1.5 1.5 0 001.3-2.2l-7.5-13a1.5 1.5 0 00-2.6 0l-7.5 13A1.5 1.5 0 004.5 19z" />
                </svg>
              </div>
              <h3 className="integrate-modal__state-title font-syne">
                {submitError === "rate-limited"
                  ? "Too many submissions"
                  : submitError === "upload-failed"
                    ? "Couldn't upload your files"
                    : "Something went wrong"}
              </h3>
              <p className="integrate-modal__state-sub font-dm">
                {submitError === "rate-limited" ? (
                  <>Please wait an hour and try again, or email us directly at <a href="mailto:team@biorlabs.com">team@biorlabs.com</a>.</>
                ) : submitError === "upload-failed" ? (
                  <>Your attached files didn&apos;t reach us. Please try again, or email us at <a href="mailto:team@biorlabs.com">team@biorlabs.com</a>.</>
                ) : (
                  <>We couldn&apos;t deliver your message. Please try again in a moment, or email us directly at <a href="mailto:team@biorlabs.com">team@biorlabs.com</a>.</>
                )}
              </p>
              <div className="integrate-modal__state-actions">
                <button type="button" className="integrate-modal__btn integrate-modal__btn--ghost" onClick={closeForm}>
                  Close
                </button>
                <button type="button" className="integrate-modal__btn integrate-modal__btn--primary" onClick={tryAgain}>
                  Try again
                </button>
              </div>
            </div>
          ) : (
            <form className="integrate-modal__form" onSubmit={onSubmit} noValidate>
              {effectiveFields.map((field, idx) => {
                const id = `im-field-${field.name}`;
                const err = errors[field.name];
                const isWide = field.type === "textarea" || field.type === "files";
                const isPaymentField = field.name === "paymentTxUrl";
                const isBudgetField = field.name === "budgetRange";
                if (isBudgetField && showNewChainNotice) {
                  return (
                    <div
                      key={field.name}
                      className={`integrate-modal__chain-request ${err ? "integrate-modal__chain-request--error" : ""}`}
                      data-testid="integrate-modal-new-chain-notice"
                    >
                      <div className="integrate-modal__chain-request-head">
                        <span className="integrate-modal__chain-request-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9.5 14.5l5-5" />
                            <path d="M14 6.5l1.3-1.3a3.5 3.5 0 114.95 4.95L18.95 11.5" />
                            <path d="M10 17.5l-1.3 1.3a3.5 3.5 0 11-4.95-4.95L5.05 12.5" />
                          </svg>
                        </span>
                        <div className="integrate-modal__chain-request-heading">
                          <h4 className="integrate-modal__chain-request-title font-syne">
                            Apply to integrate your blockchain
                          </h4>
                          <p className="integrate-modal__chain-request-sub font-dm">
                            For teams who want their blockchain supported inside BiorVault and
                            BiorSwap. Integration pricing depends on scope, so share your budget
                            and we&apos;ll come back with a tailored quote.
                          </p>
                        </div>
                      </div>
                      <div className="integrate-modal__chain-request-field">
                        <label htmlFor={id} className="integrate-modal__chain-request-label font-dm">
                          Budget range<span aria-hidden="true"> *</span>
                        </label>
                        <input
                          id={id}
                          name={field.name}
                          type="text"
                          className="integrate-modal__input integrate-modal__chain-request-input font-dm"
                          placeholder={field.placeholder}
                          value={values[field.name] || ""}
                          onChange={onChange(field.name)}
                          data-testid={`integrate-field-${field.name}`}
                          required={field.required}
                          autoComplete="off"
                        />
                        <span className="integrate-modal__chain-request-help font-dm">
                          A ballpark helps us prepare a realistic quote. Currency or stablecoin is fine.
                        </span>
                        {err ? (
                          <span className="integrate-modal__err font-dm">{err}</span>
                        ) : null}
                      </div>
                    </div>
                  );
                }
                if (isPaymentField && showPaymentNotice) {
                  return (
                    <div
                      key={field.name}
                      className={`integrate-modal__fee-notice ${err ? "integrate-modal__fee-notice--error" : ""}`}
                      data-testid="integrate-modal-fee-notice"
                    >
                      <div className="integrate-modal__fee-notice-head">
                        <span className="integrate-modal__fee-notice-eyebrow font-dm">
                          Listing fee · One-time
                        </span>
                        <span className="integrate-modal__fee-notice-amount font-syne">
                          {TOKEN_LISTING_FEE.amount}
                        </span>
                      </div>
                      <p className="integrate-modal__fee-notice-text font-dm">
                        Please send the one-time listing fee of {TOKEN_LISTING_FEE.amount} to
                        the following wallet address:
                      </p>
                      <div className="integrate-modal__wallet">
                        <code className="integrate-modal__wallet-address font-dm">
                          {TOKEN_LISTING_FEE.wallet}
                        </code>
                        <button
                          type="button"
                          onClick={copyWallet}
                          className="integrate-modal__wallet-copy font-dm"
                          aria-label="Copy wallet address"
                          data-testid="integrate-modal-copy-wallet"
                        >
                          {walletCopied ? "Copied" : "Copy"}
                        </button>
                      </div>
                      <p className="integrate-modal__fee-notice-text font-dm">
                        Once the payment is complete, paste your Etherscan transaction link
                        below so we can verify your submission.
                      </p>
                      <div className="integrate-modal__fee-notice-field">
                        <label
                          htmlFor={id}
                          className="integrate-modal__fee-notice-label font-dm"
                        >
                          Etherscan transaction link
                          <span aria-hidden="true"> *</span>
                        </label>
                        <input
                          id={id}
                          name={field.name}
                          type={field.type}
                          className="integrate-modal__input integrate-modal__fee-notice-input font-dm"
                          placeholder={field.placeholder}
                          value={values[field.name] || ""}
                          onChange={onChange(field.name)}
                          data-testid={`integrate-field-${field.name}`}
                          required={field.required}
                          autoComplete="off"
                        />
                        {err ? (
                          <span className="integrate-modal__err font-dm">{err}</span>
                        ) : null}
                      </div>
                    </div>
                  );
                }
                return (
                  <Fragment key={field.name}>
                    <div
                      className={`integrate-modal__field ${isWide ? "integrate-modal__field--wide" : ""} ${err ? "integrate-modal__field--error" : ""}`}
                    >
                      <label htmlFor={id} className="integrate-modal__label font-dm">
                        {field.label}{field.required ? <span aria-hidden="true"> *</span> : null}
                      </label>
                      {field.type === "textarea" ? (
                        <textarea
                          id={id}
                          name={field.name}
                          className="integrate-modal__input integrate-modal__input--area font-dm"
                          rows={field.rows || 4}
                          placeholder={field.placeholder}
                          value={values[field.name] || ""}
                          onChange={onChange(field.name)}
                          ref={idx === 0 ? firstFieldRef : undefined}
                          data-testid={`integrate-field-${field.name}`}
                          required={field.required}
                        />
                      ) : field.type === "select" ? (
                        <CustomSelect
                          id={id}
                          value={values[field.name] || ""}
                          options={field.options}
                          onChange={onChange(field.name)}
                          placeholder={field.placeholder}
                          required={field.required}
                          testId={`integrate-field-${field.name}`}
                          refProp={idx === 0 ? firstFieldRef : undefined}
                        />
                      ) : field.type === "files" ? (
                        <FileDropzone
                          id={id}
                          value={values[field.name]}
                          onChange={onFilesChange(field.name)}
                          maxFiles={field.maxFiles}
                          maxBytes={field.maxBytes}
                          accept={field.accept}
                          testId={`integrate-field-${field.name}`}
                        />
                      ) : (
                        <input
                          id={id}
                          name={field.name}
                          type={field.type}
                          className="integrate-modal__input font-dm"
                          placeholder={field.placeholder}
                          value={values[field.name] || ""}
                          onChange={onChange(field.name)}
                          ref={idx === 0 ? firstFieldRef : undefined}
                          data-testid={`integrate-field-${field.name}`}
                          required={field.required}
                          autoComplete="off"
                        />
                      )}
                      {err ? <span className="integrate-modal__err font-dm">{err}</span> : null}
                      {field.help ? (
                        <span className="integrate-modal__help font-dm">{field.help}</span>
                      ) : null}
                      {field.noteForValue && field.noteForValue[values[field.name]] ? (
                        <span
                          className="integrate-modal__note font-dm"
                          data-testid={`integrate-note-${field.name}`}
                        >
                          {field.noteForValue[values[field.name]]}
                        </span>
                      ) : null}
                    </div>
                  </Fragment>
                );
              })}
              {/* Trap for bots: off-screen, skipped by keyboard and screen readers,
                  no autofill. A person never fills it. */}
              <div className="integrate-modal__trap" aria-hidden="true">
                <label htmlFor="im-field-trap">Leave this field empty</label>
                <input
                  id="im-field-trap"
                  type="text"
                  name={HONEYPOT_FIELD}
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  data-testid="integrate-modal-trap"
                />
              </div>
              {turnstileOn ? (
                <div className="integrate-modal__verify">
                  <TurnstileWidget onToken={setTurnstileToken} resetKey={turnstileReset} />
                  {verifyError ? (
                    <p className="integrate-modal__err font-dm" role="alert" data-testid="integrate-modal-verify-error">
                      Please complete the verification above.
                    </p>
                  ) : null}
                </div>
              ) : null}
              <div className="integrate-modal__footer">
                <p className="integrate-modal__fineprint font-dm">
                  By submitting, you agree to BiorLabs processing this information to respond
                  to your request. We never share data with third parties.
                </p>
                <div className="integrate-modal__actions">
                  <button
                    type="button"
                    className="integrate-modal__btn integrate-modal__btn--ghost"
                    onClick={closeForm}
                    disabled={status === "submitting"}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="integrate-modal__btn integrate-modal__btn--primary"
                    data-testid="integrate-modal-submit"
                    disabled={status === "submitting" || cooling}
                  >
                    {status === "submitting" ? (
                      <>
                        <span className="integrate-modal__spinner" aria-hidden="true" />
                        Sending…
                      </>
                    ) : (
                      <>Submit request</>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default IntegrateModal;
